import {useState} from 'react'
import {Card, Spinner} from 'react-bootstrap'
import Chart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'

interface IPeriodData {
  period: string
  periodLabel: string
  policiesCount: number | null
  cancellationsCount: number | null
  neverStartedCount: number | null
}

interface IExecutiveTrendChartProps {
  periodData: IPeriodData[]
  loading: boolean
}

export const ExecutiveTrendChart = ({periodData, loading}: IExecutiveTrendChartProps) => {
  const [logScale, setLogScale] = useState(false)

  const categories = periodData.map((d) => d.periodLabel)
  const policiesData = periodData.map((d) => d.policiesCount ?? 0)
  const cancellationsData = periodData.map((d) => d.cancellationsCount ?? 0)
  const neverStartedData = periodData.map((d) => d.neverStartedCount ?? 0)

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: {show: true},
      zoom: {enabled: false},
    },
    plotOptions: {
      bar: {horizontal: false, columnWidth: '55%', borderRadius: 4},
    },
    dataLabels: {enabled: false},
    stroke: {
      show: true,
      width: [0, 3, 3],
      curve: 'smooth',
    },
    xaxis: {
      categories,
      labels: {rotate: -45, rotateAlways: false},
    },
    yaxis: [
      {
        logarithmic: logScale,
        forceNiceScale: true,
        title: {text: 'Enrollments'},
        labels: {formatter: (v: number) => (v ?? 0).toFixed(0)},
      },
      {
        opposite: true,
        logarithmic: logScale,
        forceNiceScale: true,
        title: {text: 'Cancellations / Never Started'},
        labels: {formatter: (v: number) => (v ?? 0).toFixed(0)},
      },
      {opposite: true, show: false, logarithmic: logScale, labels: {formatter: (v: number) => (v ?? 0).toFixed(0)}},
    ],
    fill: {opacity: [1, 0.85, 0.85]},
    tooltip: {
      shared: true,
      intersect: false,
      y: {formatter: (val: number) => (val ?? 0).toString()},
    },
    legend: {position: 'top', horizontalAlign: 'left'},
    colors: ['#3699ff', '#f64e60', '#ffa800'],
  }

  const series = [
    {name: 'New Enrollments', type: 'bar', data: policiesData},
    {name: 'Cancellations', type: 'line', data: cancellationsData},
    {name: 'Never Started', type: 'line', data: neverStartedData},
  ]

  return (
    <Card className='card-flush h-100 d-flex flex-column'>
      <Card.Header className='border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Monthly Enrollment Overview</span>
          <span className='text-muted fw-semibold fs-7'>
            New enrollments (bars) · cancellations · never started — by month
          </span>
        </h3>
        <div className='card-toolbar'>
          <button
            className={`btn btn-sm ${logScale ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setLogScale(v => !v)}
            title='Toggle logarithmic scale to compare months with very different volumes'
          >
            {logScale ? 'Log' : 'Linear'}
          </button>
        </div>
      </Card.Header>
      <Card.Body className='d-flex flex-column flex-grow-1' style={{minHeight: 0}}>
        {loading && (
          <div className='d-flex justify-content-center align-items-center flex-grow-1'>
            <Spinner animation='border' />
          </div>
        )}
        {!loading && periodData.length === 0 && (
          <div className='d-flex justify-content-center align-items-center flex-grow-1'>
            <p className='text-muted'>No data available for the selected period</p>
          </div>
        )}
        {!loading && periodData.length > 0 && (
          <div className='flex-grow-1' style={{minHeight: 200}}>
            <Chart options={options} series={series} type='bar' height='100%' />
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
